import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { format, parseISO, addDays } from 'date-fns';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Ban, Eye } from 'lucide-react';
import { logAdminAction } from '@/lib/auditLog';

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  paid: 'bg-blue-100 text-blue-800 border-blue-300',
  activated: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-300',
};

interface Props {
  onActivated?: () => void;
}

export function BoostOrdersPanel({ onActivated }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<any | null>(null);
  const [rejecting, setRejecting] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data: orders, isLoading } = useQuery({
    queryKey: ['boost-orders', statusFilter],
    queryFn: async () => {
      let q = supabase.from('boost_orders' as any).select('*').order('created_at', { ascending: false });
      if (statusFilter !== 'all') q = q.eq('status', statusFilter);
      const { data, error } = await q;
      if (error) throw error;
      return (data as any[]) || [];
    },
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return orders ?? [];
    const q = search.toLowerCase();
    return (orders ?? []).filter((o: any) =>
      o.id.toLowerCase().includes(q) ||
      (o.payment_ref || '').toLowerCase().includes(q) ||
      (o.user_id || '').toLowerCase().includes(q) ||
      (o.listing_id || '').toLowerCase().includes(q)
    );
  }, [orders, search]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['boost-orders'] });

  const approve = async (order: any) => {
    if (!user) return;
    // Activate: create matching featured_listing then mark order activated
    const start = new Date().toISOString().slice(0, 10);
    const end = addDays(new Date(), order.duration_days || 30).toISOString().slice(0, 10);
    const { data: fl, error: flErr } = await supabase.from('featured_listings').insert({
      listing_type: order.listing_type,
      listing_id: order.listing_id,
      tier: order.tier,
      placement: order.placement,
      priority: 0,
      start_date: start,
      end_date: end,
      price_paid: order.amount_etb || 0,
      payment_ref: order.payment_ref || null,
      auto_renew: false,
      created_by: user.id,
      is_active: true,
    } as any).select('id').single();
    if (flErr) { toast.error(flErr.message); return; }

    const { error: ordErr } = await supabase
      .from('boost_orders' as any)
      .update({
        status: 'activated',
        featured_listing_id: (fl as any)?.id,
        reviewer_id: user.id,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', order.id);
    if (ordErr) { toast.error(ordErr.message); return; }

    await logAdminAction({
      action: 'approve',
      entity_type: 'boost_order',
      entity_id: order.id,
      metadata: {
        featured_listing_id: (fl as any)?.id,
        listing_type: order.listing_type,
        listing_id: order.listing_id,
        tier: order.tier,
        placement: order.placement,
        days: order.duration_days,
        amount_etb: order.amount_etb,
      },
    });
    toast.success('Order approved & boost activated');
    refresh();
    onActivated?.();
  };

  const reject = async () => {
    if (!user || !rejecting) return;
    const { error } = await supabase
      .from('boost_orders' as any)
      .update({
        status: 'rejected',
        reviewer_id: user.id,
        reviewed_at: new Date().toISOString(),
        notes: rejectReason || null,
      })
      .eq('id', rejecting.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({
      action: 'reject',
      entity_type: 'boost_order',
      entity_id: rejecting.id,
      reason: rejectReason || undefined,
    });
    toast.success('Order rejected');
    setRejecting(null); setRejectReason('');
    refresh();
  };

  const cancel = async (order: any) => {
    if (!confirm('Cancel this order?')) return;
    const { error } = await supabase
      .from('boost_orders' as any)
      .update({
        status: 'cancelled',
        reviewer_id: user?.id ?? null,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', order.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'cancel', entity_type: 'boost_order', entity_id: order.id });
    toast.success('Order cancelled');
    refresh();
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="activated">Activated</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Search by order id, user, listing, payment ref…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-md"
          />
          <span className="text-xs text-muted-foreground ml-auto">{filtered.length} order(s)</span>
        </div>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No orders for this filter.</p>
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Listing</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Placement</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((o: any) => (
                  <TableRow key={o.id}>
                    <TableCell className="text-xs">{format(parseISO(o.created_at), 'MMM d, HH:mm')}</TableCell>
                    <TableCell className="text-xs font-mono">{(o.user_id || '').slice(0, 8)}…</TableCell>
                    <TableCell className="text-xs">
                      <span className="capitalize">{o.listing_type}</span>{' '}
                      <span className="font-mono text-muted-foreground">{(o.listing_id || '').slice(0, 8)}…</span>
                    </TableCell>
                    <TableCell className="capitalize text-sm">{o.tier}</TableCell>
                    <TableCell className="capitalize text-sm">{(o.placement || 'home').replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-sm">{o.duration_days}d</TableCell>
                    <TableCell className="text-sm">ETB {Number(o.amount_etb || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_BADGE[o.status] || ''}>{o.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setViewing(o)}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {(o.status === 'pending' || o.status === 'paid') && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => approve(o)} title="Approve & activate">
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setRejecting(o)} title="Reject">
                              <XCircle className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => cancel(o)} title="Cancel">
                              <Ban className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Details dialog */}
        <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Boost order details</DialogTitle>
              <DialogDescription>Order id <span className="font-mono">{viewing?.id?.slice(0, 8)}…</span></DialogDescription>
            </DialogHeader>
            {viewing && (
              <div className="space-y-2 text-sm">
                <Row k="Status" v={<Badge variant="outline" className={STATUS_BADGE[viewing.status] || ''}>{viewing.status}</Badge>} />
                <Row k="User" v={<span className="font-mono text-xs">{viewing.user_id}</span>} />
                <Row k="Listing" v={<span><span className="capitalize">{viewing.listing_type}</span> · <span className="font-mono text-xs">{viewing.listing_id}</span></span>} />
                <Row k="Tier / Placement" v={`${viewing.tier} / ${(viewing.placement || 'home').replace(/_/g, ' ')}`} />
                <Row k="Duration" v={`${viewing.duration_days} days`} />
                <Row k="Amount" v={`ETB ${Number(viewing.amount_etb || 0).toLocaleString()}`} />
                <Row k="Payment ref" v={viewing.payment_ref || '—'} />
                <Row k="Created" v={format(parseISO(viewing.created_at), 'PPpp')} />
                {viewing.reviewed_at && <Row k="Reviewed" v={format(parseISO(viewing.reviewed_at), 'PPpp')} />}
                {viewing.featured_listing_id && <Row k="Activated boost" v={<span className="font-mono text-xs">{viewing.featured_listing_id}</span>} />}
                {viewing.notes && <Row k="Notes" v={<span className="whitespace-pre-wrap">{viewing.notes}</span>} />}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject reason dialog */}
        <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject boost order</DialogTitle>
              <DialogDescription>Provide a reason. The user will be informed.</DialogDescription>
            </DialogHeader>
            <Textarea placeholder="Reason for rejection (optional)" value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
              <Button variant="destructive" onClick={reject}>Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/40 pb-1.5">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}
